// src/utils/votacionesUtils.js

/**
 * Normaliza la fecha de fin de una votación.
 * Si fue almacenada a las 00:00:00 (legacy/medianoche inicial),
 * la extiende a las 23:59:59.999 del mismo día para asegurar que permanezca
 * activa durante toda la jornada.
 *
 * @param {Date|Object|string} valor - Timestamp de Firestore, Date o string
 * @returns {Date|null}
 */
export function normalizarFechaFinVotacion(valor) {
    if (!valor) return null;
    const d = typeof valor.toDate === 'function'
        ? valor.toDate()
        : (valor instanceof Date ? valor : new Date(valor));

    if (isNaN(d.getTime())) return null;

    // Si la fecha fue guardada con hora 00:00:00 (legacy), ajustarla a las 23:59:59.999 de ese día
    if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
        const ajustada = new Date(d);
        ajustada.setHours(23, 59, 59, 999);
        return ajustada;
    }
    return d;
}

/**
 * Determina el estado de una votación ('activa', 'programada' o 'vencida').
 *
 * @param {Object} votacion
 * @returns {string} 'activa' | 'programada' | 'vencida'
 */
export function calcularEstadoVotacionEfectivo(votacion = {}) {
    const ahora = new Date();
    const inicio = votacion.fechaCreacion
        ? (typeof votacion.fechaCreacion.toDate === 'function' ? votacion.fechaCreacion.toDate() : new Date(votacion.fechaCreacion))
        : null;
    const fin = normalizarFechaFinVotacion(votacion.fechaFin);

    if (inicio && ahora < inicio) {
        return 'programada';
    }
    if (fin && ahora >= fin) {
        return 'vencida';
    }
    if (inicio && fin && ahora >= inicio && ahora < fin) {
        return 'activa';
    }
    return votacion.estado || 'activa';
}

/**
 * Calcula la meta objetivo de participantes para una votación.
 * Prioriza las casas o domicilios habilitados explícitos de la votación sobre el total del fraccionamiento.
 *
 * @param {number} explicitTarget - Total global de residentes del fraccionamiento
 * @param {Object} votacion - Objeto de la votación
 * @returns {number} Meta de participantes calculada
 */
export function getParticipationTarget(explicitTarget = 0, votacion = {}) {
    // 1. Si la votación tiene casas habilitadas específicas (array de IDs)
    if (Array.isArray(votacion.casasHabilitadas) && votacion.casasHabilitadas.length > 0) {
        return votacion.casasHabilitadas.length;
    }

    // 2. Si la votación tiene números de casas habilitadas
    if (Array.isArray(votacion.numerosCasasHabilitadas) && votacion.numerosCasasHabilitadas.length > 0) {
        return votacion.numerosCasasHabilitadas.length;
    }

    // 3. Si la votación tiene una meta numérica de participantes configurada
    const metaNumerica = Number(votacion.metaParticipantes);
    if (Number.isFinite(metaNumerica) && metaNumerica > 0) {
        return Math.round(metaNumerica);
    }

    // 4. Candidatos secundarios dentro de la votación
    const candidates = [
        votacion.totalParticipantes,
        votacion.objetivoParticipacion,
        votacion.participantesObjetivo,
        votacion.metaParticipacion,
        votacion.totalResidentes,
        votacion.totalCasas,
    ];

    for (const candidate of candidates) {
        const val = Number(candidate);
        if (Number.isFinite(val) && val > 0) {
            return Math.round(val);
        }
    }

    // 5. Fallback al total general de residentes del fraccionamiento
    const fallback = Number(explicitTarget);
    return Number.isFinite(fallback) && fallback > 0 ? Math.round(fallback) : 0;
}

/**
 * Verifica si un usuario residente está habilitado para ver y participar en una votación.
 * Si la votación no especifica casas ni domicilios restringidos, está abierta a todo el fraccionamiento.
 *
 * @param {Object} votacion - Documento de la votación
 * @param {Object} userData - Datos del usuario en sesión
 * @param {Object} casaActiva - Casa activa seleccionada en el contexto de autenticación
 * @param {Array} misCasas - Lista de casas vinculadas al usuario
 * @returns {boolean} true si está habilitado o si es administrador
 */
export function esResidenteHabilitadoParaVotacion(votacion = {}, userData = null, casaActiva = null, misCasas = []) {
    const casasHabilitadas = votacion.casasHabilitadas;
    const numerosHabilitados = votacion.numerosCasasHabilitadas;

    const tieneRestriccionCasas = (Array.isArray(casasHabilitadas) && casasHabilitadas.length > 0) ||
                                  (Array.isArray(numerosHabilitados) && numerosHabilitados.length > 0);

    // Si no tiene restricción de domicilios, está abierta a toda la comunidad
    if (!tieneRestriccionCasas) {
        return true;
    }

    // Los administradores siempre tienen visibilidad para gestionar y auditar
    const role = String(userData?.rol || '').toLowerCase();
    if (['superadmin', 'admin', 'manager', 'plataforma', 'admin_fracc', 'administrator', 'administrador', 'platformadmin'].includes(role)) {
        return true;
    }

    // Recopilar todos los identificadores posibles de la(s) casa(s) del residente
    const misIdentificadores = new Set();

    if (casaActiva?.id) misIdentificadores.add(String(casaActiva.id));
    if (casaActiva?.numero) misIdentificadores.add(String(casaActiva.numero).trim().toLowerCase());

    if (userData?.casaId) misIdentificadores.add(String(userData.casaId));
    if (userData?.casaID) misIdentificadores.add(String(userData.casaID));
    if (userData?.idCasa) misIdentificadores.add(String(userData.idCasa));
    if (userData?.numeroCasa) misIdentificadores.add(String(userData.numeroCasa).trim().toLowerCase());
    if (userData?.casaNumero) misIdentificadores.add(String(userData.casaNumero).trim().toLowerCase());
    if (userData?.numero) misIdentificadores.add(String(userData.numero).trim().toLowerCase());
    if (userData?.casa?.numero) misIdentificadores.add(String(userData.casa.numero).trim().toLowerCase());
    if (userData?.casaAsignada?.numero) misIdentificadores.add(String(userData.casaAsignada.numero).trim().toLowerCase());

    if (Array.isArray(misCasas)) {
        misCasas.forEach((c) => {
            if (c?.id) misIdentificadores.add(String(c.id));
            if (c?.numero) misIdentificadores.add(String(c.numero).trim().toLowerCase());
        });
    }

    // Comprobar contra casasHabilitadas (IDs de documentos o números)
    if (Array.isArray(casasHabilitadas)) {
        for (const ch of casasHabilitadas) {
            const valStr = String(ch).trim().toLowerCase();
            if (misIdentificadores.has(valStr) || misIdentificadores.has(String(ch))) {
                return true;
            }
        }
    }

    // Comprobar contra numerosCasasHabilitadas
    if (Array.isArray(numerosHabilitados)) {
        for (const nh of numerosHabilitados) {
            const valStr = String(nh).trim().toLowerCase();
            if (misIdentificadores.has(valStr)) {
                return true;
            }
        }
    }

    return false;
}
